import React, { useState } from 'react';
import { ApplicationData } from '../LoanApplicationFlow';
import '../StepStyles.css';

interface Step2PersonalDetailsProps {
  data: ApplicationData;
  onUpdate: (data: Partial<ApplicationData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const Step2PersonalDetails: React.FC<Step2PersonalDetailsProps> = ({
  data,
  onUpdate,
  onNext,
  onBack
}) => {
  const [formData, setFormData] = useState({
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    phoneNumber: data.phoneNumber,
    saIdNumber: data.saIdNumber
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValidating, setIsValidating] = useState(false);

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateSaIdNumber = (idNumber: string): boolean => {
    // Basic SA ID validation (13 digits)
    if (!/^\d{13}$/.test(idNumber)) return false;
    
    // Luhn algorithm check
    const digits = idNumber.split('').map(Number);
    let sum = 0;
    
    for (let i = 0; i < 13; i++) {
      let digit = digits[i];
      if (i % 2 === 1) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
    }
    
    return sum % 10 === 0;
  };

  const validatePhoneNumber = (phone: string): boolean => {
    // SA phone number validation
    const phoneRegex = /^(\+27|0)[6-8][0-9]{8}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  // Handle input changes
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Update parent data
    onUpdate({ [field]: value });
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }
    
    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Phone number validation
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!validatePhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid SA phone number (e.g., 0123456789)';
    }
    
    // SA ID validation
    if (!formData.saIdNumber.trim()) {
      newErrors.saIdNumber = 'SA ID number is required';
    } else if (!validateSaIdNumber(formData.saIdNumber)) {
      newErrors.saIdNumber = 'Please enter a valid SA ID number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle continue
  const handleContinue = async () => {
    setIsValidating(true);
    
    if (validateForm()) {
      // Update parent with current data
      onUpdate(formData);
      
      // Simulate API validation
      await new Promise(resolve => setTimeout(resolve, 500));
      
      onNext();
    }
    
    setIsValidating(false);
  };

  return (
    <div className="step-container">
      <div className="step-header">
        <h2 className="step-title">Verify Your Personal Details</h2>
        <p className="step-description">
          Please confirm your personal information. This must match your ID document and bank account.
        </p>
      </div>

      <div className="step-content">
        <form className="personal-details-form" onSubmit={(e) => e.preventDefault()}>
          {/* Name Fields */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName" className="form-label">
                First Name *
              </label>
              <input
                type="text"
                id="firstName"
                className={`form-input ${errors.firstName ? 'error' : ''}`}
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                placeholder="Enter your first name"
              />
              {errors.firstName && (
                <span className="error-message">{errors.firstName}</span>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="lastName" className="form-label">
                Last Name *
              </label>
              <input
                type="text"
                id="lastName"
                className={`form-input ${errors.lastName ? 'error' : ''}`}
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                placeholder="Enter your last name"
              />
              {errors.lastName && (
                <span className="error-message">{errors.lastName}</span>
              )}
            </div>
          </div>

          {/* Contact Fields */}
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              className={`form-input ${errors.email ? 'error' : ''}`}
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="your.email@example.com"
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
            <span className="field-hint">
              We'll use this to send you loan updates and confirmations
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber" className="form-label">
              Phone Number *
            </label>
            <input
              type="tel"
              id="phoneNumber"
              className={`form-input ${errors.phoneNumber ? 'error' : ''}`}
              value={formData.phoneNumber}
              onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
              placeholder="0123456789"
            />
            {errors.phoneNumber && (
              <span className="error-message">{errors.phoneNumber}</span>
            )}
            <span className="field-hint">
              This must match the number you registered with
            </span>
          </div>

          {/* ID Number */}
          <div className="form-group">
            <label htmlFor="saIdNumber" className="form-label">
              SA ID Number *
            </label>
            <input
              type="text"
              id="saIdNumber"
              className={`form-input ${errors.saIdNumber ? 'error' : ''}`}
              value={formData.saIdNumber}
              onChange={(e) => handleInputChange('saIdNumber', e.target.value)}
              placeholder="0000000000000"
              maxLength={13}
            />
            {errors.saIdNumber && (
              <span className="error-message">{errors.saIdNumber}</span>
            )}
            <span className="field-hint">
              This must match your South African ID document exactly
            </span>
          </div>
        </form>

        {/* Important Information */}
        <div className="modern-info-section">
          <div className="info-header">
            <div className="info-header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <circle cx="12" cy="16" r="1"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <div className="info-header-text">
              <h4>Privacy & Security</h4>
              <p>Your information is protected with bank-grade security</p>
            </div>
          </div>
          
          <div className="info-items">
            <div className="info-item">
              <div className="info-item-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <circle cx="12" cy="16" r="1"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <div className="info-item-content">
                <span className="info-item-title">Encrypted Storage</span>
                <span className="info-item-description">Your personal information is encrypted and secure</span>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-item-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4"/>
                  <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9s4.03-9 9-9c1.51 0 2.93.37 4.18 1.03"/>
                </svg>
              </div>
              <div className="info-item-content">
                <span className="info-item-title">Purpose-Limited Use</span>
                <span className="info-item-description">We only use your details for loan processing and verification</span>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-item-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14,2 14,8 20,8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
              </div>
              <div className="info-item-content">
                <span className="info-item-title">Official Verification</span>
                <span className="info-item-description">Your information will be verified against official records</span>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-item-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M9 12l2 2 4-4"/>
                </svg>
              </div>
              <div className="info-item-content">
                <span className="info-item-title">POPIA Compliant</span>
                <span className="info-item-description">We comply with POPIA (Protection of Personal Information Act)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step Actions */}
      <div className="step-actions">
        <button
          className="back-btn secondary"
          onClick={onBack}
          type="button"
        >
          Back
        </button>
        
        <button
          className="continue-btn primary"
          onClick={handleContinue}
          disabled={isValidating}
          type="button"
        >
          {isValidating ? (
            <>
              <span className="btn-spinner"></span>
              Validating...
            </>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14"/>
                <path d="M12 5l7 7-7 7"/>
              </svg>
              Continue to Bank Details
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default Step2PersonalDetails;

